import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPlus, FiMinus, FiShoppingCart } from 'react-icons/fi';
import Modal from './Modal';
import Image from 'next/image';
import { useQuote } from '../contexts/QuoteContext';

const ProductDetailModal = ({ isOpen, onClose, product, onAddToQuote }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [rentalPeriod, setRentalPeriod] = useState(3);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { addToQuote } = useQuote();

  useEffect(() => {
    if (product && product.type === 'variable' && product.variations) {
      // Find the first enabled variation as default
      const defaultVariant = product.variations.find(v => v.isEnabled);
      setSelectedVariant(defaultVariant || null);

      // Initialize selected attributes
      const initialAttributes = {};
      if (defaultVariant && defaultVariant.attributes) {
        defaultVariant.attributes.forEach(attr => {
          initialAttributes[attr.name] = attr.value;
        });
      }
      setSelectedAttributes(initialAttributes);
    }

    // Reset image index when product changes
    setCurrentImageIndex(0);
  }, [product]);

  if (!product) return null;

  // Handle images - they might be strings (URLs) or objects with url property
  const images = product.images || [];
  const processedImages = images.map(image => {
    if (typeof image === 'string') {
      // Convert string URL to object format
      return { url: image, alt: product.name };
    }
    return image; // Already an object
  });
  const currentImage = processedImages[Math.min(currentImageIndex, processedImages.length - 1)] || null;

  const handleAttributeChange = (attributeName, value) => {
    const newSelectedAttributes = { ...selectedAttributes, [attributeName]: value };
    setSelectedAttributes(newSelectedAttributes);

    // Find matching variation
    if (product.type === 'variable' && product.variations) {
      const matchingVariant = product.variations.find(variant => {
        return variant.isEnabled && variant.attributes.every(attr =>
          newSelectedAttributes[attr.name] === attr.value
        );
      });
      setSelectedVariant(matchingVariant || null);
    }
  };

  const handleAddToQuote = () => {
    const quoteItem = {
      product: product._id || product.id,
      variant: selectedVariant?._id,
      quantity,
      rentalPeriod,
      unitPrice: selectedVariant ? selectedVariant.price : product.price,
      totalPrice: (selectedVariant ? selectedVariant.price : product.price) * quantity * rentalPeriod,
      productData: {
        name: product.name,
        images: processedImages,
        category: product.category
      },
      variantData: selectedVariant ? {
        attributes: selectedVariant.attributes,
        price: selectedVariant.price
      } : null
    };

    addToQuote(quoteItem);
    onClose();
    if (onAddToQuote) {
      onAddToQuote();
    }
  };

  const currentPrice = selectedVariant ? selectedVariant.price : product.price;
  const minPrice = product.type === 'variable' && product.priceRange
    ? product.priceRange.min
    : currentPrice || 0;
  const priceDisplay = `Prices from $${minPrice.toFixed(2)}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="95%" title={product.name}>
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Product Image */}
        <div className="space-y-4">
          <div className="relative h-96 bg-neutral-100 rounded-lg overflow-hidden">
            {currentImage ? (
              <Image
                src={currentImage.url.replace('http://127.0.0.1:5001', '')}
                alt={currentImage.alt || product.name}
                fill
                className="object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-6xl text-neutral-300">🌸</div>
              </div>
            )}
          </div>

          {/* Image Navigation */}
          {processedImages.length > 1 && (
            <div className="space-y-3">
              {/* Dot Indicators */}
              <div className="flex justify-center gap-2">
                {processedImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentImageIndex
                        ? 'bg-primary-500'
                        : 'bg-neutral-300 hover:bg-neutral-400'
                    }`}
                  />
                ))}
              </div>

              {/* Thumbnail Images */}
              <div className="grid grid-cols-4 gap-2">
                {processedImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`relative h-20 bg-neutral-100 rounded-lg overflow-hidden transition-all ${
                      index === currentImageIndex
                        ? 'ring-2 ring-primary-500 ring-offset-2'
                        : 'hover:ring-2 hover:ring-neutral-300 hover:ring-offset-2'
                    }`}
                  >
                    <Image
                      src={image.url.replace('http://127.0.0.1:5001', '')}
                      alt={image.alt || `${product.name} ${index + 1}`}
                      fill
                      className="object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="space-y-6">
          {/* Category */}
          <div>
            <span className="text-sm font-semibold text-primary-600 uppercase tracking-wide">
              {typeof product.category === 'object' ? product.category?.name : product.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-neutral-800">{product.name}</h1>

          {/* Description */}
          <p className="text-neutral-600 leading-relaxed">{product.description}</p>

          {/* Price */}
          <div className="text-2xl font-semibold text-primary-600">
            {priceDisplay}
            {product.priceUnit && product.priceUnit !== 'per_item' && (
              <span className="text-sm text-neutral-500 ml-2">{product.priceUnit.replace('_', ' ')}</span>
            )}
          </div>

          {/* Variations */}
          {product.type === 'variable' && product.attributes && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                {product.attributes.filter(attr => attr.isVariation)[0]?.name || 'Options'}
              </h3>
              {product.attributes.filter(attr => attr.isVariation).map((attribute) => (
                <div key={attribute.attributeId || attribute.name}>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {attribute.name}
                  </label>
                  <select
                    value={selectedAttributes[attribute.name] || ''}
                    onChange={(e) => handleAttributeChange(attribute.name, e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select {attribute.name.toLowerCase()}</option>
                    {attribute.values?.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {/* Quantity and Rental Period */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Quantity
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-8 h-8 rounded bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center"
                >
                  <FiMinus size={14} />
                </button>
                <span className="w-12 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-8 h-8 rounded bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center"
                >
                  <FiPlus size={14} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Rental Period (Days)
              </label>
              <select
                value={rentalPeriod}
                onChange={(e) => setRentalPeriod(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value={1}>1 day</option>
                <option value={2}>2 days</option>
                <option value={3}>3 days</option>
                <option value={7}>1 week</option>
                <option value={14}>2 weeks</option>
              </select>
            </div>
          </div>

          {/* Add to Quote Button */}
          <button
            onClick={handleAddToQuote}
            className="w-full py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 hover:shadow-lg hover:-translate-y-0.5 bg-primary-500 hover:bg-primary-600 text-white"
          >
            <FiShoppingCart size={20} />
            Add To Quote
          </button>

          {/* Additional Info */}
          {product.careInstructions && (
            <div className="pt-4 border-t border-neutral-200">
              <h4 className="font-semibold text-neutral-800 mb-2">Care Instructions</h4>
              <p className="text-sm text-neutral-600">{product.careInstructions}</p>
            </div>
          )}
        </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProductDetailModal;